import {
  DB_NAME,
  DB_VERSION,
  STORES,
  type CachedPreOrder,
  type SyncChange,
} from "./schema";

export class OfflineDatabase {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  // Monotonic open token. Each `init()` claims the current value; `close()`
  // bumps it. An in-flight open whose token no longer matches when it resolves
  // is stale — it closes the freshly-opened handle instead of reinstating
  // `this.db`, so a `close()` that races an unresolved `init()` still leaves the
  // connection closed (otherwise the pending `onsuccess` would silently re-open
  // it and re-block the subsequent deleteDatabase).
  private openToken = 0;
  private readonly MAX_ENTRIES = 1000; // Max cached pre-orders
  private readonly EXPIRY_DAYS = 30;

  // Deferred initialization (called on first use)
  async init(): Promise<void> {
    if (this.db) return;
    if (this.initPromise) return this.initPromise;

    const token = ++this.openToken;
    this.initPromise = new Promise((resolve, reject) => {
      // Check browser support
      if (typeof window === "undefined" || !("indexedDB" in window)) {
        reject(new Error("IndexedDB not available"));
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // PreOrders store with indexes
        if (!db.objectStoreNames.contains(STORES.PRE_ORDERS)) {
          const store = db.createObjectStore(STORES.PRE_ORDERS, {
            keyPath: "key",
          });
          store.createIndex("date", "date", { unique: false });
          store.createIndex("preOrderId", "preOrderId", { unique: false });
          store.createIndex("accessedAt", "accessedAt", { unique: false });
          store.createIndex("expiresAt", "expiresAt", { unique: false });
        }

        // SyncQueue store with indexes
        if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
          const store = db.createObjectStore(STORES.SYNC_QUEUE, {
            keyPath: "id",
          });
          store.createIndex("preOrderId", "preOrderId", { unique: false });
          store.createIndex("flowId", "flowId", { unique: false });
          store.createIndex("status", "status", { unique: false });
          store.createIndex("createdAt", "createdAt", { unique: false });
        }

        // Metadata store for app state
        if (!db.objectStoreNames.contains(STORES.METADATA)) {
          db.createObjectStore(STORES.METADATA, { keyPath: "key" });
        }

        // Partners store for offline partner creation
        if (!db.objectStoreNames.contains(STORES.PARTNERS)) {
          const store = db.createObjectStore(STORES.PARTNERS, {
            keyPath: "id",
          });
          store.createIndex("isOffline", "isOffline", { unique: false });
          store.createIndex("createdAt", "createdAt", { unique: false });
        }

        // Chat sessions store
        if (!db.objectStoreNames.contains(STORES.CHAT_SESSIONS)) {
          const store = db.createObjectStore(STORES.CHAT_SESSIONS, {
            keyPath: "id",
          });
          store.createIndex("syncId", "syncId", { unique: false });
        }

        // Chat messages store
        if (!db.objectStoreNames.contains(STORES.CHAT_MESSAGES)) {
          const store = db.createObjectStore(STORES.CHAT_MESSAGES, {
            keyPath: "id",
          });
          store.createIndex("syncId", "syncId", { unique: false });
          store.createIndex("sessionId", "sessionId", { unique: false });
        }

        // Chat executions store
        if (!db.objectStoreNames.contains(STORES.CHAT_EXECUTIONS)) {
          const store = db.createObjectStore(STORES.CHAT_EXECUTIONS, {
            keyPath: "id",
          });
          store.createIndex("syncId", "syncId", { unique: false });
          store.createIndex("sessionId", "sessionId", { unique: false });
        }

        // Chat media store
        if (!db.objectStoreNames.contains(STORES.CHAT_MEDIA)) {
          const store = db.createObjectStore(STORES.CHAT_MEDIA, {
            keyPath: "id",
          });
          store.createIndex("syncId", "syncId", { unique: false });
          store.createIndex("sessionId", "sessionId", { unique: false });
          store.createIndex("messageId", "messageId", { unique: false });
        }
      };

      request.onsuccess = () => {
        // A close() landed while this open was in flight — discard the stale
        // handle rather than reinstating a connection the caller asked to drop.
        if (token !== this.openToken) {
          request.result.close();
          resolve();
          return;
        }
        this.db = request.result;
        resolve();
      };

      request.onerror = () => {
        reject(request.error || new Error("Failed to open IndexedDB"));
      };
    });

    return this.initPromise;
  }

  // Close the underlying IndexedDB connection so a pending
  // `indexedDB.deleteDatabase(DB_NAME)` (issued by the offline-storage reset on
  // logout / user-switch) is no longer blocked by this long-lived handle. The
  // next store operation re-opens lazily via `init()`. Without this the legacy
  // connection stays open for the tab's lifetime and blocks the delete —
  // silently on Chromium, but hanging the reset on Firefox.
  close(): void {
    // Invalidate any in-flight init() so its onsuccess closes its own handle
    // instead of reinstating this.db (see openToken).
    this.openToken++;
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    this.initPromise = null;
  }

  // Generic get
  async get<T>(storeName: string, key: string): Promise<T | null> {
    await this.init();
    if (!this.db) return null;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeName, "readonly");
      const store = tx.objectStore(storeName);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => reject(request.error);
    });
  }

  // Generic set (put)
  async set<T>(storeName: string, entry: T): Promise<void> {
    await this.init();
    if (!this.db) throw new Error("Database not initialized");

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeName, "readwrite");
      const store = tx.objectStore(storeName);
      const request = store.put(entry);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Generic delete
  async delete(storeName: string, key: string): Promise<void> {
    await this.init();
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeName, "readwrite");
      const store = tx.objectStore(storeName);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Query by index
  async queryByIndex<T>(
    storeName: string,
    indexName: string,
    value: IDBValidKey,
  ): Promise<T[]> {
    await this.init();
    if (!this.db) return [];

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeName, "readonly");
      const store = tx.objectStore(storeName);
      const index = store.index(indexName);
      const request = index.getAll(value);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Get all entries from store
  async getAll<T>(storeName: string): Promise<T[]> {
    await this.init();
    if (!this.db) return [];

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeName, "readonly");
      const store = tx.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Count entries
  async count(storeName: string): Promise<number> {
    await this.init();
    if (!this.db) return 0;

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(storeName, "readonly");
      const store = tx.objectStore(storeName);
      const request = store.count();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Get LRU keys for eviction
  async getLRUKeys(count: number): Promise<string[]> {
    await this.init();
    if (!this.db) return [];

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORES.PRE_ORDERS, "readonly");
      const store = tx.objectStore(STORES.PRE_ORDERS);
      const index = store.index("accessedAt");
      const request = index.openCursor(); // Ascending (oldest first)

      const keys: string[] = [];

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor && keys.length < count) {
          keys.push(cursor.value.key);
          cursor.continue();
        } else {
          resolve(keys);
        }
      };

      request.onerror = () => reject(request.error);
    });
  }

  // Get expired keys
  async getExpiredKeys(): Promise<string[]> {
    await this.init();
    if (!this.db) return [];

    const now = Date.now();

    return new Promise((resolve, reject) => {
      const tx = this.db!.transaction(STORES.PRE_ORDERS, "readonly");
      const store = tx.objectStore(STORES.PRE_ORDERS);
      const index = store.index("expiresAt");
      const range = IDBKeyRange.upperBound(now);
      const request = index.getAll(range);

      request.onsuccess = () => {
        resolve(request.result.map((entry: CachedPreOrder) => entry.key));
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Get pre-order IDs with pending changes
  private async getPendingPreOrderIds(): Promise<Set<string>> {
    const pending = await this.getAll<SyncChange>(STORES.SYNC_QUEUE);
    const preOrderIds = new Set<string>();

    pending.forEach((change) => {
      if (change.entityType === "pre-order") {
        preOrderIds.add(change.entityId);
      } else if (change.entityType === "flow" && change.data.pre_order_id) {
        preOrderIds.add(change.data.pre_order_id);
      }
    });

    return preOrderIds;
  }

  // Enforce quota (call after writes)
  async enforceQuota(aggressive = false): Promise<void> {
    try {
      // Step 1: Clean expired entries
      const expiredKeys = await this.getExpiredKeys();
      await Promise.all(
        expiredKeys.map((key) => this.delete(STORES.PRE_ORDERS, key)),
      );

      // Step 2: Check count
      const count = await this.count(STORES.PRE_ORDERS);

      // Step 3: Evict if needed
      if (count > this.MAX_ENTRIES || aggressive) {
        const evictionPercent = aggressive ? 0.5 : 0.2;
        const toEvict = Math.ceil(count * evictionPercent);
        const lruKeys = await this.getLRUKeys(toEvict);

        // Don't evict entries with pending changes
        const pendingPreOrderIds = await this.getPendingPreOrderIds();
        const safeToEvict = lruKeys.filter((key) => {
          const preOrderId = key.split(":")[1];
          return !pendingPreOrderIds.has(preOrderId);
        });

        await Promise.all(
          safeToEvict.map((key) => this.delete(STORES.PRE_ORDERS, key)),
        );
      }
    } catch (error) {}
  }
}
